import React, { useEffect, useState } from "react";
import {
  IconCancelBlue,
  IconKeyboardArrowRightBlue,
  IconUpload2,
} from "assets/svg";
import { Button, Image, message, Upload, UploadProps } from "antd";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { Product, Transaction, TransactionParams } from "common/interfaces";
import {
  fetchTransactionDetail,
  transactionCleanData,
  updateTransaction,
} from "reducers/transactionSlice";
import { fetchProductDetail, productCleanData } from "reducers/productSlice";
import { getLabelFromValue, productPrice } from "common/utils";
import { MAX_FILE_SIZE, SHIP_CHARGE_TYPE } from "common/constants";
import { FILE_BASE_URL } from "config/apiConfig";
import { TransactionStatusEnum } from "common/enums/TransactionStatus.enum";
import { uploadCleanData, uploadImage } from "reducers/uploadSlice";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import Chat from "components/Chat";
import CustomConfirmModal from "components/CustomConfirmModal";
import { useCommonContext } from "contexts/CommonContext";

const SELLER_CANCEL_REQUEST_ALLOWED_STATUSES = [
  TransactionStatusEnum.ACCEPT,
  TransactionStatusEnum.DELIVERED_TO_SHIP,
] as const;

const TransactionDetail = () => {
  const { setHeaderTitle, setHeaderTitleMobile } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const { userData } = useSelector((state: RootState) => state.auth);
  const {
    transactionDetailData,
    updateTransactionData,
    error: transactionErr,
  } = useSelector((state: RootState) => state.transaction);
  const { productDetailData, error: productErr } = useSelector(
    (state: RootState) => state.product,
  );
  const { uploadImageData, error: uploadErr } = useSelector(
    (state: RootState) => state.upload,
  );
  const productDetailInfo: Product | undefined = productDetailData?.data;
  const transactionDetailInfo: Transaction | undefined =
    transactionDetailData?.data;
  const controller = new AbortController();
  const { code, buyerId, sellerId } = useParams();
  const [openConfirmModalReject, setOpenConfirmModalReject] = useState(false);
  const [openConfirmModalCancel, setOpenConfirmModalCancel] = useState(false);
  const [openConfirmModalApprove, setOpenConfirmModalApprove] = useState(false);
  const [openConfirmModalComplete, setOpenConfirmModalComplete] =
    useState(false);

  const transactionParams: TransactionParams = {
    product_id: Number(productDetailInfo?.id),
    buyer_id: Number(buyerId),
    seller_id: Number(sellerId),
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    const latestFileList = newFileList.slice(-1);
    const formData = new FormData();
    const latestFile = latestFileList[0]?.originFileObj;
    if (latestFile) {
      formData.append("file", latestFile);
      dispatch(uploadImage(formData, "product", controller.signal));
    }
  };

  const beforeUpload = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      message.error("※PDF推奨（最大10MB）");
      return Upload.LIST_IGNORE;
    }
    if (file.type !== "application/pdf") {
      message.error(`${file.name} 有効なPDFファイルではありません`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const reject = () => {
    dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.REJECT,
      }),
    );
    setOpenConfirmModalReject(false);
  };

  const cancel = () => {
    dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.SELLER_REQUEST_CANCEL,
      }),
    );
    setOpenConfirmModalCancel(false);
  };

  const approve = () => {
    dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.SELLER_ACCEPT_CANCEL,
      }),
    );
    setOpenConfirmModalApprove(false);
  };

  const complete = async () => {
    await dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.COMPLETED,
      }),
    );
    setOpenConfirmModalComplete(false);
  };

  const displayStatus = () => {
    if (transactionDetailInfo?.status === TransactionStatusEnum.NEGOTIATE) {
      return "この商品は出品中です。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.REQUEST
    ) {
      return "この商品は購入申請中です。";
    } else if (transactionDetailInfo?.status === TransactionStatusEnum.ACCEPT) {
      return "この商品は発送待ちです。";
    } else if (transactionDetailInfo?.status === TransactionStatusEnum.REJECT) {
      return "取引がキャンセルされました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.DELIVERED_TO_SHIP
    ) {
      return "この商品は受け取り待ちです。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.RECEIVED
    ) {
      return "この商品は受け取りが完了しました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.INVOICE_ISSUED
    ) {
      return "請求書が発行されました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.COMPLETED
    ) {
      return "取引が完了しました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.SELLER_REQUEST_CANCEL
    ) {
      return "キャンセル申請中です。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.SELLER_ACCEPT_CANCEL
    ) {
      return "取引がキャンセルされました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.BUYER_REQUEST_CANCEL
    ) {
      return "買い手企業からキャンセル申請が届きました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.BUYER_ACCEPT_CANCEL
    ) {
      return "取引がキャンセルされました。";
    }
  };

  const displaySubmit = () => {
    if (transactionDetailInfo?.status === TransactionStatusEnum.REQUEST) {
      return (
        <Button
          block
          type="primary"
          htmlType="button"
          className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
          onClick={() =>
            dispatch(
              updateTransaction({
                ...transactionParams,
                status: TransactionStatusEnum.ACCEPT,
              }),
            )
          }
        >
          購入を承認する
        </Button>
      );
    } else if (transactionDetailInfo?.status === TransactionStatusEnum.ACCEPT) {
      return (
        <Button
          block
          type="primary"
          htmlType="button"
          className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
          onClick={() =>
            dispatch(
              updateTransaction({
                ...transactionParams,
                status: TransactionStatusEnum.DELIVERED_TO_SHIP,
              }),
            )
          }
        >
          商品を発送済みにする
        </Button>
      );
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.RECEIVED
    ) {
      return (
        <>
          <Upload
            onChange={handleChange}
            beforeUpload={beforeUpload}
            maxCount={1}
          >
            <Button
              block
              type="primary"
              htmlType="button"
              className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
            >
              請求書をアップロードする
            </Button>
          </Upload>
          <div className="w-full md:w-[610px] h-[19px] text-black text-center font-['Noto_Sans_JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
            ※PDF推奨（最大10MB）
          </div>
        </>
      );
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.INVOICE_ISSUED
    ) {
      return (
        <>
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
            onClick={() => setOpenConfirmModalComplete(true)}
          >
            取引を完了する
          </Button>
          <div className="w-full md:w-[610px] h-[19px] text-black text-center font-['Noto_Sans_JP'] text-[11px] md:text-[12px] not-italic font-normal leading-[160%]">
            ※必ず支払いの確認ができてから取引を完了してください。
          </div>
          <div className="flex flex-col items-center gap-[10px]">
            <Link
              to={`${FILE_BASE_URL}/${transactionDetailInfo?.invoice_url}`}
              target="_blank"
            >
              <Button
                block
                type="primary"
                htmlType="button"
                className="w-full min-h-16 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-[16px] md:text-[18px] md:text-xl font-bold whitespace-normal"
              >
                {`${userData.company_name}_請求書.pdf`}
              </Button>
            </Link>
          </div>
        </>
      );
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.BUYER_REQUEST_CANCEL
    ) {
      return (
        <Button
          block
          type="primary"
          htmlType="button"
          className="w-full md:!w-[323px] min-h-16 bg-red-dark text-white text-[18px] md:text-xl font-bold"
          onClick={() => setOpenConfirmModalApprove(true)}
        >
          キャンセル申請を承認する
        </Button>
      );
    }
  };

  const displayCancel = () => {
    if (transactionDetailInfo?.status === TransactionStatusEnum.REQUEST) {
      return (
        <div className="self-stretch border-t border-white flex flex-col justify-start items-center gap-2.5">
          <div
            data-プロパティ1="base + icon"
            className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
            onClick={() => setOpenConfirmModalReject(true)}
          >
            <IconCancelBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              購入を拒否する
            </div>
          </div>
        </div>
      );
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.INVOICE_ISSUED
    ) {
      return (
        <Upload
          onChange={handleChange}
          beforeUpload={beforeUpload}
          showUploadList={false}
        >
          <div className="self-stretch border-t border-white flex flex-col justify-start items-center gap-2.5">
            <div
              data-プロパティ1="base + icon"
              className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
            >
              <IconUpload2 className="w-[20px] h-[20px] aspect-[1/1]" />
              <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                請求書を再発行する
              </div>
            </div>
          </div>
        </Upload>
      );
    } else if (
      SELLER_CANCEL_REQUEST_ALLOWED_STATUSES.some(
        (status) => status === transactionDetailInfo?.status,
      )
    ) {
      return (
        <div className="self-stretch border-t border-white flex flex-col justify-start items-center gap-2.5">
          <div
            data-プロパティ1="base + icon"
            className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
            onClick={() => setOpenConfirmModalCancel(true)}
          >
            <IconCancelBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              この取引をキャンセルする
            </div>
          </div>
        </div>
      );
    }
  };

  useEffect(() => {
    if (
      (productDetailInfo?.id && buyerId && sellerId) ||
      updateTransactionData?.success
    ) {
      dispatch(fetchTransactionDetail(transactionParams));
    }
  }, [productDetailInfo?.id, buyerId, sellerId, updateTransactionData]);

  useEffect(() => {
    if (code) {
      dispatch(fetchProductDetail(code, controller.signal));
    }
  }, [code]);

  useEffect(() => {
    if (uploadImageData?.success) {
      dispatch(
        updateTransaction({
          ...transactionParams,
          status: TransactionStatusEnum.INVOICE_ISSUED,
          invoice_url: uploadImageData?.data.path,
        }),
      );
    }
  }, [uploadImageData]);

  useEffect(() => {
    if (transactionErr) {
      message.error(transactionErr);
    }
  }, [transactionErr]);

  useEffect(() => {
    if (productErr) {
      message.error(productErr);
    }
  }, [productErr]);

  useEffect(() => {
    if (uploadErr) {
      message.error(uploadErr);
    }
  }, [uploadErr]);

  useEffect(() => {
    setHeaderTitle(
      `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
    );
    setHeaderTitleMobile(`${productDetailInfo?.product_name}`);
    return () => {
      setHeaderTitleMobile("");
    };
  }, [productDetailInfo, setHeaderTitle, setHeaderTitleMobile]);

  useEffect(() => {
    dispatch(uploadCleanData());
    return () => {
      dispatch(transactionCleanData());
      dispatch(productCleanData());
      dispatch(uploadCleanData());
    };
  }, []);

  return (
    <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[40px] md:gap-10 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "商品検索",
            link: "/seller/product/list",
          },
          {
            label: `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
            link: `/seller/product/${productDetailInfo?.code}`,
          },
          {
            label: "取引チャット",
            isCurrent: true,
          },
        ]}
      />
      <div className="self-stretch flex flex-col justify-start items-start gap-12">
        <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-start items-start gap-12">
          <div
            data-プロパティ1="base"
            className="w-full md:w-72 inline-flex flex-col justify-center items-start"
          >
            <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
              <div className="w-16 h-16 p-2.5 bg-primary-light rounded-md inline-flex flex-col justify-center items-center gap-2.5">
                <Image
                  src={`${FILE_BASE_URL}/${productDetailInfo?.images[0]?.image_url}`}
                  className="!w-full !h-full object-cover"
                />
              </div>
              <div className="flex-1 justify-start text-black text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                {productDetailInfo?.product_name}
              </div>
              <IconKeyboardArrowRightBlue className="w-[16px] h-[16px] aspect-[1/1]" />
            </div>
            <div className="self-stretch flex flex-col justify-start items-start">
              <div className="self-stretch py-3.5 bg-white border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  販売価格
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  {productPrice(productDetailInfo)}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  送料
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  {getLabelFromValue(
                    SHIP_CHARGE_TYPE,
                    productDetailInfo?.shipping_charge_type,
                  )}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  出品ID
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  #{productDetailInfo?.id}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  取引ID
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  #{transactionDetailInfo?.id}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex-1 inline-flex flex-col justify-start items-start gap-10">
            <div className="self-stretch p-7 bg-primary-light rounded-xl flex flex-col justify-center items-center gap-7">
              <div className="self-stretch px-5 py-7 bg-white rounded-md flex flex-col justify-start items-center gap-5">
                <div className="self-stretch flex-col md:flex-row inline-flex justify-center items-center gap-3">
                  <div
                    data-プロパティ1="border - bold"
                    className="min-w-14 px-2.5 py-[5px] bg-white rounded-md outline outline-2 outline-offset-[-2px] outline-primary inline-flex flex-col justify-center items-center gap-2.5"
                  >
                    <div className="text-center justify-start text-primary text-[11px] md:text-xs font-bold font-['Noto_Sans_JP'] leading-tight">
                      ステータス
                    </div>
                  </div>
                  <div className="justify-start text-black text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                    {displayStatus()}
                  </div>
                </div>
                {displaySubmit()}
                {displayCancel()}
              </div>
              <div className="w-full flex flex-col justify-center items-start gap-2.5">
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    買い手企業
                  </div>
                  {transactionDetailInfo?.buyer.code ? (
                    <Link
                      to={`/seller/information?tab=profile&companyCode=${encodeURIComponent(
                        transactionDetailInfo.buyer.code,
                      )}`}
                      className="flex-1 justify-start text-primary text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug hover:underline"
                    >
                      {transactionDetailInfo.buyer.company_name}
                    </Link>
                  ) : (
                    <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                      {transactionDetailInfo?.buyer.company_name}
                    </div>
                  )}
                </div>
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    所在地
                  </div>
                  <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    {transactionDetailInfo?.buyer.address}
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    電話番号
                  </div>
                  <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    {transactionDetailInfo?.buyer.phone_number}
                  </div>
                </div>
              </div>
            </div>
            {buyerId && sellerId ? (
              <Chat
                buyerId={Number(buyerId)}
                sellerId={Number(sellerId)}
                productId={productDetailInfo?.id}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalReject}
        title="この取引を拒否しますか?"
        onOk={reject}
        onCancel={() => setOpenConfirmModalReject(false)}
        okText="拒否要求"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openConfirmModalCancel}
        title="この取引をキャンセル申請しますか？"
        description={`この取引のキャンセルを買い手企業に申請します。
          双方の合意によってキャンセルが成立するまで、
          取引は保留状態となります。`}
        note={`
          ※一度キャンセルを申請すると、取り消すことはできません。
          ※出品者の承認をもってキャンセルが成立します。`}
        onOk={cancel}
        onCancel={() => setOpenConfirmModalCancel(false)}
        okText="キャンセルを申請する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openConfirmModalApprove}
        title="キャンセル申請を承認しますか？"
        description={`買い手企業からのキャンセル申請を承認すると、
          この取引は完全にキャンセルとなります。`}
        note={`
          ※この操作は取り消すことができません。
          ※取引履歴には「キャンセル」として記録されます。`}
        onOk={approve}
        onCancel={() => setOpenConfirmModalApprove(false)}
        okText="キャンセルを承認する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openConfirmModalComplete}
        title="この取引を完了しても
          よろしいですか？"
        description={`この操作を行うと、取引は完了済みとなります。
          双方の合意をもって、商品・代金のやり取りが無事に
          完了していることをご確認の上、操作してください。`}
        note={`
          ※完了後は内容の変更やキャンセルはできません。
          ※取引完了は履歴として記録されます。`}
        onOk={complete}
        onCancel={() => setOpenConfirmModalComplete(false)}
        okText="取引を完了する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </div>
  );
};

export default TransactionDetail;
